<template>
  <v-form ref="form" v-model="valid">
    <v-alert :value="showErrorOnSave" type="error">{{ errorOnSave }}</v-alert>
    <v-container grid-list-md>
      <v-layout wrap>
        <v-flex xs6>
          <v-layout wrap>
            <v-flex xs12>
              <v-text-field
                v-model.trim="edited.text"
                required
                :rules="[required]"
                label="Word"
                :error-messages="errorMessages"
                :loading="loading"
                :disabled="disabled"
              ></v-text-field>
            </v-flex>
            <v-flex xs12>
              <v-select
                v-model="edited.categoryIds"
                :disabled="disabled"
                :items="categories"
                :rules="[required]"
                multiple
                item-text="fullName"
                item-value="id"
                label="Category"
              ></v-select>
            </v-flex>
            <v-flex xs12>
              <AudioInput v-model="edited.pronunciation" :suggest="edited.text" style="display: none"></AudioInput>
            </v-flex>
          </v-layout>
        </v-flex>
        <v-flex xs6>
          <v-layout wrap>
            <v-flex xs12>
              <v-text-field
                :value="english"
                label="In English"
                :disabled="true"
              ></v-text-field>
            </v-flex>
            <v-flex xs12>
              <v-text-field
                :value="russian"
                label="In Russian"
                :disabled="true"
              ></v-text-field>
            </v-flex>
            <v-flex xs12>
              <ImageInput v-model="edited.image" :suggest="edited.text" :aspect-ratio="1"/>
            </v-flex>
          </v-layout>
        </v-flex>
      </v-layout>
    </v-container>
    <v-card-actions>
      <v-spacer></v-spacer>
      <v-btn color="blue darken-1" flat @click.native="close">Cancel</v-btn>
      <v-btn color="blue darken-1" flat @click.native="save" :loading="saving">Add</v-btn>
    </v-card-actions>
    </v-form>
</template>

<script>
import { TranslatorApi, WordsApi } from '../common/api'
import { ADD_WORD } from '../store/mutation-types'
import { mapGetters, mapState } from 'vuex'
import ImageInput from './ImageInput'
import find from 'lodash/find'
import intersectionBy from 'lodash/intersectionBy'
import debounce from 'lodash/debounce'
import AudioInput from './AudioInput'
import { actual } from '../util'

const translate = actual(TranslatorApi.translate)

export default {
  components: { AudioInput, ImageInput },
  props: ['baseWord', 'baseCategories'],
  data () {
    return {
      valid: false,
      saving: false,
      loading: false,
      errorMessages: [],
      errorOnSave: 'Error has occurred while saving',
      showErrorOnSave: false,
      edited: {},
      russian: '',
      english: ''
    }
  },
  computed: {
    disabled () {
      return this.saving || this.loading
    },
    ...mapState([
      'categories'
    ]),
    ...mapGetters([
      'sourceLanguageId'
    ])
  },
  watch: {
    'edited.text' () {
      this.debouncedTranslateAll()
    },
    baseWord () {
      this.showErrorOnSave = false
      this.$refs.form.resetValidation()
      this.import()
    }
  },
  created () {
    this.debouncedTranslateAll = debounce(this.translateAll, 300)
    this.import()
  },
  methods: {
    import () {
      if (!this.baseWord) {
        return
      }
      this.edited = {
        text: '',
        baseWord: this.baseWord.text,
        pronunciation: null,
        image: this.baseWord.localizedImage ? null : this.baseWord.image,
        localizedImage: this.baseWord.localizedImage,
        languageId: this.sourceLanguageId,
        categoryIds: this.resolveCategories()
      }
      this.translateWord()
    },
    resolveCategories () {
      return intersectionBy(
        this.categories,
        this.baseCategories.filter(({ id }) => this.baseWord.categoryIds.includes(id)),
        cat => cat.fullName
      ).map(cat => cat.id)
    },
    validate () {
      return this.$refs.form.validate()
    },
    close () {
      if (this.saving) {
        return
      }
      this.$emit('close')
    },
    async save () {
      if (this.saving) {
        return
      }
      if (!this.validate()) {
        return
      }
      this.showErrorOnSave = false
      this.saving = true
      try {
        if (!await this.checkDuplicates(this.edited)) {
          throw new Error('Disable to add duplicate')
        }

        const { data } = await WordsApi.create(this.edited)
        this.$store.commit(ADD_WORD, data)

        this.saving = false
        this.$emit('imported')
      } catch (e) {
        this.saving = false
        this.showErrorOnSave = true
        console.error(e)
      }
    },
    required (value) {
      return !!value || 'Required.'
    },
    async checkDuplicates (word) {
      const { data } = await WordsApi.duplicates(this.$store.state.sourceDictionary, word.text)
      if (data.total === 0) {
        return true
      }

      if (data.total === 1 && data.items[0].id === word.id) {
        return true
      }

      const categories = this.categories
        .filter(({ id }) => find(data.items, { categoryId: id }) !== undefined)
        .map(cat => cat.name)

      if (confirm('Данное слово уже присутствует в категориях: ' + categories.join(', ') + '. Вы уверены что хотите добавить?')) {
        return true
      }

      return false
    },
    translateAll () {
      this.russian = null
      this.english = null

      if (!this.edited.text) {
        return
      }

      if (this.edited.languageId !== 'ru') {
        translate(() => [this.edited.text, this.edited.languageId, 'ru'])
          .then(({ data }) => { this.russian = data.text })
          .catch(e => alert(e))
      }

      if (this.edited.languageId !== 'en') {
        translate(() => [this.edited.text, this.edited.languageId, 'en'])
          .then(({ data }) => { this.english = data.text })
          .catch(e => alert(e))
      }
    },
    async translateWord () {
      this.loading = true
      try {
        const { data } = await translate(() => [this.baseWord.text, 'en', this.edited.languageId])
        this.edited.text = data.text
      } catch (e) {
        console.error(e)
        alert(e)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>
